<script setup lang="ts">
import {computed, PropType} from "vue";
import {ILearningStylesReportData, LearningStylesLabel} from "@/pages/Insights/ReportsDetailPage/reportTypes";
import LearningStylesMetricBars from "@/pages/Insights/ReportsDetailPage/components/LearningStylesMetricBars.vue";

defineProps({
  reportData: {
    type: Array as PropType<ILearningStylesReportData[]>,
    required: true
  },
  loadingData: {
    type: Boolean,
    required: true
  }
});

const headers = computed(
  () => [
    {
      title: "User",
      key: "fullName"
    },

    {
      title: "Tenant",
      key: "tenant"
    },

    {
      title: "Data vs Conceptual",
      key: "results.data_conceptual.score"
    },
    {
      title: "Doing vs Thinking",
      key: "results.doing_thinking.score"
    },
    {
      title: "Optic vs Written",
      key: "results.optic_written.score"
    },
    {
      title: "Structured vs Fluid",
      key: "results.structured_fluid.score"
    },
  ]
);

const labels: Record<LearningStylesLabel, string> = {
  balanced: "Balanced",
  moderately_data: "Moderately Data",
  moderately_conceptual: "Moderately Conceptual",
  moderately_doing: "Moderately Doing",
  moderately_thinking: "Moderately Thinking",
  moderately_optic: "Moderately Optic",
  moderately_written: "Moderately Written",
  moderately_structured: "Moderately Structured",
  moderately_fluid: "Moderately Fluid",
  highly_conceptual: "Highly Conceptual",
  highly_data: "Highly Data",
  highly_doing: "Highly Doing",
  highly_thinking: "Highly Thinking",
  highly_optic: "Highly Optic",
  highly_written: "Highly Written",
  highly_structured: "Highly Structured",
  highly_fluid: "Highly Fluid",
  no_results: "No Results",
};

</script>

<template>
  <v-data-table
    :items="reportData"
    :headers="headers"
    :loading="loadingData"
    :items-per-page="50"
  >

    <template v-slot:item.fullName="{ item }">
      <span class="font-weight-bold">{{ item.fullName }}</span>

      <div>
        <div v-if="item.email">
          <v-icon size="x-small">far fa-envelope</v-icon>
          {{ item.email }}
        </div>
        <div v-if="item.phone">
          <v-icon size="x-small">far fa-phone</v-icon>
          {{ item.phone }}
        </div>
      </div>
    </template>

    <template v-slot:item.tenant="{ item }">
      <div class="py-4">
        <div class="font-weight-bold text-blue-grey-darken-1 pb-1">{{ item.tenant.title }}</div>
        <div>
          <v-chip v-if="item.institution.title" size="x-small" class="font-weight-bold mr-3">
            <v-icon color="grey" class="mr-2">far fa-building</v-icon>
            {{ item.institution.title }}
          </v-chip>
          <v-chip v-if="item.cohort.title" size="x-small" class="font-weight-bold">
            <v-icon color="grey" class="mr-2">far fa-graduation-cap</v-icon>
            {{ item.cohort.title }}
          </v-chip>
        </div>
      </div>
    </template>

    <template v-slot:[`item.results.data_conceptual.score`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <div class="d-flex justify-space-between text-caption">
            <div>Data</div>
            <div>Conceptual</div>
          </div>
          <learning-styles-metric-bars :score="item.results.data_conceptual.score" color="orange" />
          <v-chip variant="outlined" size="x-small" class="mt-2 font-weight-bold">{{ labels[item.results.data_conceptual.label] }}</v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.doing_thinking.score`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <div class="d-flex justify-space-between text-caption">
            <div>Doing</div>
            <div>Thinking</div>
          </div>
          <learning-styles-metric-bars :score="item.results.doing_thinking.score" />
          <v-chip variant="outlined" size="x-small" class="mt-2 font-weight-bold">{{ labels[item.results.doing_thinking.label] }}</v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.optic_written.score`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <div class="d-flex justify-space-between text-caption">
            <div>Optic</div>
            <div>Written</div>
          </div>
          <learning-styles-metric-bars :score="item.results.optic_written.score" color="purple" />
          <v-chip variant="outlined" size="x-small" class="mt-2 font-weight-bold">{{ labels[item.results.optic_written.label] }}</v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.structured_fluid.score`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <div class="d-flex justify-space-between text-caption">
            <div>Structured</div>
            <div>Fluid</div>
          </div>
          <learning-styles-metric-bars :score="item.results.structured_fluid.score" color="green" />
          <v-chip variant="outlined" size="x-small" class="mt-2 font-weight-bold">{{ labels[item.results.structured_fluid.label] }}</v-chip>
        </div>
      </div>
    </template>

  </v-data-table>
</template>

<style scoped>

</style>
